import store from '../../state';
import { WildflowerService, WildflowerPoiProfile } from '../../services/WildflowerService';
import { getElevationAt } from '../../services/PoiDataService';

export class WildflowerPanel {
  private lastPoiKey: string = '';
  private currentElevation: number = 1600;
  public onClose?: () => void;

  public setOnClose(cb: () => void) {
    this.onClose = cb;
  }

  public render(state: any, productKey: string = 'alpine_wildflowers'): string {
    const poi = state.selectedPoi;
    const lat = poi ? poi.lat : state.mapCenter[1];
    const lon = poi ? poi.lng : state.mapCenter[0];
    const elev = poi && poi.elev ? poi.elev : this.currentElevation;
    const elevMeters = elev / 3.28084;

    const defaultInfo = WildflowerService.getDefaultDateInfo(productKey);
    let monthIndex = defaultInfo.monthIndex;
    let selectedDateStr = defaultInfo.dateStr;
    let viewMode = 'peak_date';
    let domain = 'na';
    let speciesId = 'composite';

    if (productKey === 'desert_wildflowers') {
      monthIndex = state.desertWildflowerMonthIndex !== undefined ? state.desertWildflowerMonthIndex : defaultInfo.monthIndex;
      selectedDateStr = state.desertWildflowerSelectedDate || defaultInfo.dateStr;
      viewMode = state.desertWildflowerViewMode || 'peak_date';
      domain = state.desertWildflowerDomain || 'na';
      speciesId = state.desertWildflowerSpecies || 'composite';
    } else if (productKey === 'wild_berries') {
      monthIndex = state.wildBerryMonthIndex !== undefined ? state.wildBerryMonthIndex : defaultInfo.monthIndex;
      selectedDateStr = state.wildBerrySelectedDate || defaultInfo.dateStr;
      viewMode = state.wildBerryViewMode || 'peak_date';
      domain = state.wildBerryDomain || 'na';
      speciesId = state.wildBerrySpecies || 'composite';
    } else {
      monthIndex = state.alpineWildflowerMonthIndex !== undefined ? state.alpineWildflowerMonthIndex : (state.wildflowerMonthIndex !== undefined ? state.wildflowerMonthIndex : defaultInfo.monthIndex);
      selectedDateStr = state.alpineWildflowerSelectedDate || state.wildflowerSelectedDate || defaultInfo.dateStr;
      viewMode = state.alpineWildflowerViewMode || state.wildflowerViewMode || 'peak_date';
      domain = state.alpineWildflowerDomain || state.wildflowerDomain || 'na';
      speciesId = state.alpineWildflowerSpecies || state.wildflowerSpecies || 'composite';
    }
    const year = 2026;

    const locKey = `${lat.toFixed(4)}_${lon.toFixed(4)}`;
    if (locKey !== this.lastPoiKey) {
      this.lastPoiKey = locKey;
      if (poi && poi.elev && poi.elev > 0) {
        this.currentElevation = poi.elev;
      } else {
        getElevationAt(lat, lon).then(prof => {
          if (prof && prof.elevation > 0 && prof.elevation !== this.currentElevation) {
            this.currentElevation = prof.elevation;
            store.update({ selectedPoi: { ...poi, elev: prof.elevation } });
          }
        });
      }
    }

    const { days, profile, firstDayCol, monthName } = WildflowerService.getMonthCalendar(
      monthIndex,
      lat,
      lon,
      elevMeters,
      speciesId,
      year,
      productKey
    );

    const weekendDates = WildflowerService.getWeekendDates();

    // 1. Domain Switcher
    let domainSwitcherHtml = '';
    if (productKey === 'desert_wildflowers') {
      domainSwitcherHtml = `
        <div style="display: flex; gap: 6px; width: 100%; margin-bottom: 6px;">
          <div style="flex: 1; padding: 6px 8px; font-size: 0.70rem; font-weight: 700; border-radius: 6px; border: 1px solid var(--color-primary, #e91e63); background: rgba(233, 30, 99, 0.2); color: #fff; display: flex; align-items: center; justify-content: center; gap: 6px;">
            <span>🏜️</span> <span>Arid Southwest Basins (2.5km)</span>
          </div>
        </div>
      `;
    } else if (productKey === 'wild_berries') {
      domainSwitcherHtml = `
        <div style="display: flex; gap: 6px; width: 100%; margin-bottom: 6px;">
          <button 
            class="gw-wf-domain-btn ${domain === 'na' ? 'active' : ''}" 
            data-domain="na"
            title="High-Resolution North America Stack (NBM 2.5km + SNODAS + URMA)"
            style="flex: 1; padding: 6px 4px; font-size: 0.70rem; font-weight: 700; border-radius: 6px; border: 1px solid ${domain === 'na' ? '#ab47bc' : 'rgba(255,255,255,0.1)'}; background: ${domain === 'na' ? 'rgba(171, 71, 188, 0.25)' : 'rgba(15, 23, 42, 0.6)'}; color: ${domain === 'na' ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; justify-content: center; gap: 5px;">
            <span>🏔️</span> <span>North America (2.5km)</span>
          </button>
          <button 
            class="gw-wf-domain-btn ${domain === 'global' ? 'active' : ''}" 
            data-domain="global"
            title="Global Mountain Berry Coverage (DWD ICON 13km + CMC GDPS)"
            style="flex: 1; padding: 6px 4px; font-size: 0.70rem; font-weight: 700; border-radius: 6px; border: 1px solid ${domain === 'global' ? '#29b6f6' : 'rgba(255,255,255,0.1)'}; background: ${domain === 'global' ? 'rgba(41, 182, 246, 0.25)' : 'rgba(15, 23, 42, 0.6)'}; color: ${domain === 'global' ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; justify-content: center; gap: 5px;">
            <span>🌐</span> <span>Global Mountain</span>
          </button>
        </div>
      `;
    } else {
      domainSwitcherHtml = `
        <div style="display: flex; gap: 6px; width: 100%; margin-bottom: 6px;">
          <button 
            class="gw-wf-domain-btn ${domain === 'na' ? 'active' : ''}" 
            data-domain="na"
            title="High-Resolution North America Stack (NBM 2.5km + SNODAS + URMA)"
            style="flex: 1; padding: 6px 4px; font-size: 0.70rem; font-weight: 700; border-radius: 6px; border: 1px solid ${domain === 'na' ? 'var(--color-primary, #e91e63)' : 'rgba(255,255,255,0.1)'}; background: ${domain === 'na' ? 'rgba(233, 30, 99, 0.25)' : 'rgba(15, 23, 42, 0.6)'}; color: ${domain === 'na' ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; justify-content: center; gap: 5px;">
            <span>🏔️</span> <span>North America (2.5km)</span>
          </button>
          <button 
            class="gw-wf-domain-btn ${domain === 'global' ? 'active' : ''}" 
            data-domain="global"
            title="Global Alpine Coverage (DWD ICON 13km + CMC GDPS)"
            style="flex: 1; padding: 6px 4px; font-size: 0.70rem; font-weight: 700; border-radius: 6px; border: 1px solid ${domain === 'global' ? '#29b6f6' : 'rgba(255,255,255,0.1)'}; background: ${domain === 'global' ? 'rgba(41, 182, 246, 0.25)' : 'rgba(15, 23, 42, 0.6)'}; color: ${domain === 'global' ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; justify-content: center; gap: 5px;">
            <span>🌐</span> <span>Global Alpine</span>
          </button>
        </div>
      `;
    }

    // 2. View Mode Switcher
    let modeSwitcherHtml = '';
    if (productKey === 'desert_wildflowers') {
      modeSwitcherHtml = `
        <div style="display: grid; grid-template-columns: 1fr 1.25fr 1fr; gap: 4px; background: rgba(0,0,0,0.35); padding: 3px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.08); margin-bottom: 8px;">
          <button class="gw-wf-mode-btn" data-mode="peak_date" style="padding: 6px 2px; font-size: 0.66rem; font-weight: 700; border-radius: 5px; border: 1px solid ${viewMode === 'peak_date' ? '#ffd54f' : 'transparent'}; background: ${viewMode === 'peak_date' ? 'rgba(255, 213, 79, 0.25)' : 'transparent'}; color: ${viewMode === 'peak_date' ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.15s ease; text-align: center;">
            📅 Peak Date
          </button>
          <button class="gw-wf-mode-btn" data-mode="status" style="padding: 6px 2px; font-size: 0.66rem; font-weight: 700; border-radius: 5px; border: 1px solid ${viewMode === 'status' ? '#e91e63' : 'transparent'}; background: ${viewMode === 'status' ? 'rgba(233, 30, 99, 0.25)' : 'transparent'}; color: ${viewMode === 'status' ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.15s ease; text-align: center;">
            🌸 Daily Stage
          </button>
          <button class="gw-wf-mode-btn" data-mode="mbii" style="padding: 6px 2px; font-size: 0.66rem; font-weight: 700; border-radius: 5px; border: 1px solid ${viewMode === 'mbii' ? '#ff9800' : 'transparent'}; background: ${viewMode === 'mbii' ? 'rgba(255, 152, 0, 0.25)' : 'transparent'}; color: ${viewMode === 'mbii' ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.15s ease; text-align: center;">
            ✨ BII Score
          </button>
        </div>
      `;
    } else if (productKey === 'wild_berries') {
      modeSwitcherHtml = `
        <div style="display: grid; grid-template-columns: 1fr 1.25fr 1fr; gap: 4px; background: rgba(0,0,0,0.35); padding: 3px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.08); margin-bottom: 8px;">
          <button class="gw-wf-mode-btn" data-mode="peak_date" style="padding: 6px 2px; font-size: 0.66rem; font-weight: 700; border-radius: 5px; border: 1px solid ${viewMode === 'peak_date' ? '#ffd54f' : 'transparent'}; background: ${viewMode === 'peak_date' ? 'rgba(255, 213, 79, 0.25)' : 'transparent'}; color: ${viewMode === 'peak_date' ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.15s ease; text-align: center;">
            📅 Peak Harvest
          </button>
          <button class="gw-wf-mode-btn" data-mode="status" style="padding: 6px 2px; font-size: 0.66rem; font-weight: 700; border-radius: 5px; border: 1px solid ${viewMode === 'status' ? '#ab47bc' : 'transparent'}; background: ${viewMode === 'status' ? 'rgba(171, 71, 188, 0.25)' : 'transparent'}; color: ${viewMode === 'status' ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.15s ease; text-align: center;">
            🫐 Ripening Stage
          </button>
          <button class="gw-wf-mode-btn" data-mode="mbii" style="padding: 6px 2px; font-size: 0.66rem; font-weight: 700; border-radius: 5px; border: 1px solid ${viewMode === 'mbii' ? '#00e676' : 'transparent'}; background: ${viewMode === 'mbii' ? 'rgba(0, 230, 118, 0.25)' : 'transparent'}; color: ${viewMode === 'mbii' ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.15s ease; text-align: center;">
            🏅 Crop Quality
          </button>
        </div>
      `;
    } else {
      modeSwitcherHtml = `
        <div style="display: grid; grid-template-columns: 1fr 1.25fr 1fr; gap: 4px; background: rgba(0,0,0,0.35); padding: 3px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.08); margin-bottom: 8px;">
          <button class="gw-wf-mode-btn" data-mode="peak_date" style="padding: 6px 2px; font-size: 0.66rem; font-weight: 700; border-radius: 5px; border: 1px solid ${viewMode === 'peak_date' ? '#ffd54f' : 'transparent'}; background: ${viewMode === 'peak_date' ? 'rgba(255, 213, 79, 0.25)' : 'transparent'}; color: ${viewMode === 'peak_date' ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.15s ease; text-align: center;">
            📅 Peak Date
          </button>
          <button class="gw-wf-mode-btn" data-mode="status" style="padding: 6px 2px; font-size: 0.66rem; font-weight: 700; border-radius: 5px; border: 1px solid ${viewMode === 'status' ? '#e91e63' : 'transparent'}; background: ${viewMode === 'status' ? 'rgba(233, 30, 99, 0.25)' : 'transparent'}; color: ${viewMode === 'status' ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.15s ease; text-align: center;">
            🌸 Daily Status
          </button>
          <button class="gw-wf-mode-btn" data-mode="mbii" style="padding: 6px 2px; font-size: 0.66rem; font-weight: 700; border-radius: 5px; border: 1px solid ${viewMode === 'mbii' ? '#ff9800' : 'transparent'}; background: ${viewMode === 'mbii' ? 'rgba(255, 152, 0, 0.25)' : 'transparent'}; color: ${viewMode === 'mbii' ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.15s ease; text-align: center;">
            ✨ Intensity
          </button>
        </div>
      `;
    }

    // 3. Species Dropdown
    let speciesOptions: Array<{ id: string; name: string }>;
    if (productKey === 'desert_wildflowers') {
      speciesOptions = [
        { id: 'composite', name: 'Broad Basin Superbloom (All Annuals)' },
        { id: 'desert_gold', name: 'Desert Gold / Sunflower (Death Valley)' },
        { id: 'ca_poppy', name: 'California Poppy (Antelope Valley)' },
        { id: 'desert_dandelion', name: 'Desert Dandelion (Anza-Borrego)' },
        { id: 'phacelia', name: 'Notch-leaf Phacelia (Desert Washes)' },
        { id: 'desert_lupine', name: 'Desert / Arizona Lupine (Sonoran)' }
      ];
    } else if (productKey === 'wild_berries') {
      speciesOptions = [
        { id: 'composite', name: 'Composite Ripening Wave (All Species)' },
        { id: 'serviceberry', name: 'Serviceberry / Juneberry (Midwest & Plains)' },
        { id: 'salmonberry', name: 'Salmonberry (Coastal PNW)' },
        { id: 'thimbleberry', name: 'Thimbleberry (Montane Canyons)' },
        { id: 'blueberry', name: 'Lowbush Wild Blueberry (Boreal / Northeast)' },
        { id: 'huckleberry', name: 'Mountain Huckleberry (Subalpine)' },
        { id: 'blackberry', name: 'Wild Blackberry & Dewberry (Continental)' },
        { id: 'elderberry', name: 'American Black Elderberry (Eastern / Central)' }
      ];
    } else {
      speciesOptions = [
        { id: 'composite', name: 'Meadow & Foothill Wave (All Species)' },
        { id: 'woodland_trillium', name: 'Coast & Lowland Trillium (Early Forest)' },
        { id: 'balsamroot', name: 'Arrowleaf Balsamroot (Gorge & Montane)' },
        { id: 'rhododendron_coast', name: 'Pacific Rhododendron & Foxglove' },
        { id: 'lupine_paintbrush', name: 'Subalpine Lupine & Paintbrush' },
        { id: 'avalanche_lily', name: 'Avalanche Lily (Snow Margins)' }
      ];
    }

    const speciesDropdownHtml = `
      <div style="display: flex; flex-direction: column; gap: 4px; margin-bottom: 8px;">
        <label style="font-size: 0.64rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.5px;">
          ${productKey === 'desert_wildflowers' ? 'Desert Species / Basin Target' : (productKey === 'wild_berries' ? 'Berry Species Target' : 'Species Target / Mask')}
        </label>
        <select class="gw-wf-species-select" style="background: rgba(15, 23, 42, 0.8); border: 1px solid rgba(255,255,255,0.15); border-radius: 6px; color: #fff; font-size: 0.72rem; padding: 6px 8px; font-weight: 600; outline: none; cursor: pointer;">
          ${speciesOptions.map(sp => `<option value="${sp.id}" ${sp.id === speciesId ? 'selected' : ''}>${sp.name}</option>`).join('')}
        </select>
      </div>
    `;

    // 4. Quick Jump Buttons (for Daily Status Mode)
    const quickJumpHtml = viewMode === 'status' ? `
      <div style="display: flex; gap: 4px; margin-bottom: 8px;">
        <button class="gw-wf-quick-btn" data-date="${defaultInfo.dateStr}" style="flex: 1; padding: 5px 2px; font-size: 0.64rem; font-weight: 700; border-radius: 5px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); color: #fff; cursor: pointer;">
          Today
        </button>
        <button class="gw-wf-quick-btn" data-date="${weekendDates.thisSaturday}" style="flex: 1.25; padding: 5px 2px; font-size: 0.64rem; font-weight: 700; border-radius: 5px; background: rgba(233,30,99,0.2); border: 1px solid rgba(233,30,99,0.4); color: #ff80ab; cursor: pointer;">
          This Weekend (Sat)
        </button>
        <button class="gw-wf-quick-btn" data-date="${weekendDates.nextSaturday}" style="flex: 1.25; padding: 5px 2px; font-size: 0.64rem; font-weight: 700; border-radius: 5px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); color: #fff; cursor: pointer;">
          Next Weekend
        </button>
      </div>
    ` : '';

    // 5. Month Tabs HTML
    let monthTabLabels: string[];
    if (productKey === 'desert_wildflowers') {
      monthTabLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May'];
    } else if (productKey === 'wild_berries') {
      monthTabLabels = ['Jun', 'Jul', 'Aug', 'Sep', 'Oct'];
    } else {
      monthTabLabels = ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'];
    }

    const monthHeaderDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const tabsHtml = monthTabLabels.map((tabLabel, idx) => {
      const isActive = idx === monthIndex;
      return `
        <button 
          class="gw-wf-month-tab ${isActive ? 'active' : ''}" 
          data-month-idx="${idx}"
          style="flex: 1; padding: 5px 2px; font-size: 0.65rem; font-weight: 700; border-radius: 5px; border: 1px solid ${isActive ? 'var(--color-primary, #e91e63)' : 'rgba(255,255,255,0.1)'}; background: ${isActive ? 'rgba(233, 30, 99, 0.25)' : 'rgba(15, 23, 42, 0.6)'}; color: ${isActive ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.2s ease; text-align: center;">
          ${tabLabel}
        </button>
      `;
    }).join('');

    // 6. Calendar Grid Cells
    let calendarGridHtml = `
      <div style="display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 2px; font-size: 0.62rem; text-align: center; font-weight: 700; color: var(--text-muted); margin-bottom: 4px; width: 100%; box-sizing: border-box;">
        ${monthHeaderDays.map(d => `<div style="min-width: 0; box-sizing: border-box; text-align: center; overflow: hidden;">${d}</div>`).join('')}
      </div>
      <div style="display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 2px; width: 100%; box-sizing: border-box;">
    `;

    for (let i = 0; i < firstDayCol; i++) {
      calendarGridHtml += `<div style="height: 38px; min-width: 0; background: rgba(255,255,255,0.02); border-radius: 4px; box-sizing: border-box;"></div>`;
    }

    days.forEach(day => {
      const isSelected = day.dateStr === selectedDateStr;
      const activeBorder = isSelected ? '2px solid #ffffff' : '1px solid rgba(255,255,255,0.12)';
      const activeShadow = isSelected ? '0 0 8px rgba(255,255,255,0.4)' : 'none';
      const todayDot = day.isToday ? `<span style="position: absolute; top: 1px; right: 2px; font-size: 0.50rem; color: #ffeb3b;">●</span>` : '';

      calendarGridHtml += `
        <div 
          class="gw-wf-day-cell" 
          data-date="${day.dateStr}"
          title="${day.dateStr}: ${day.stageName} (${day.concurrencyPct}% Concurrency)"
          style="position: relative; height: 38px; min-width: 0; width: 100%; background: ${day.colorHex}; border-radius: 4px; border: ${activeBorder}; box-shadow: ${activeShadow}; cursor: pointer; display: flex; flex-direction: column; justify-content: space-between; padding: 2px 1px; box-sizing: border-box; transition: transform 0.15s ease; overflow: hidden; user-select: none;">
          ${todayDot}
          <div style="font-size: 0.66rem; font-weight: 800; color: #ffffff; text-shadow: 0 1px 2px rgba(0,0,0,0.8); line-height: 1; padding-left: 1px;">
            ${day.dayNum}
          </div>
          <div style="font-size: 0.46rem; font-weight: 700; text-align: center; color: #fff; text-shadow: 0 1px 2px rgba(0,0,0,0.9); text-transform: uppercase; background: rgba(0,0,0,0.35); border-radius: 2px; padding: 1px 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; width: 100%; box-sizing: border-box; letter-spacing: -0.2px;">
            ${day.badgeLabel}
          </div>
        </div>
      `;
    });
    calendarGridHtml += `</div>`;

    // 7. Multi-Species Trend Chart (SVG)
    const trendChartSvg = this.renderTrendChart(profile, productKey);

    // 8. Legend Scale HTML
    let legendScaleHtml = '';
    if (viewMode === 'status') {
      if (productKey === 'desert_wildflowers') {
        legendScaleHtml = `
          <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 8px 10px; margin-top: 8px;">
            <div style="font-size: 0.64rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; margin-bottom: 6px;">
              Desert Wildflower Phenophase Scale
            </div>
            <div style="height: 10px; border-radius: 4px; background: linear-gradient(90deg, #64748b 0%, #81c784 20%, #cddc39 40%, #e91e63 60%, #ff9800 80%, #90a4ae 100%); border: 1px solid rgba(255,255,255,0.12); margin-bottom: 4px;"></div>
            <div style="display: grid; grid-template-columns: repeat(6, 1fr); font-size: 0.58rem; text-align: center; font-weight: 700; color: var(--text-muted);">
              <span>Seed</span>
              <span>Rosette</span>
              <span>Early</span>
              <span style="color: #ff4081;">Peak</span>
              <span>Past</span>
              <span style="color: #94a3b8;">Dormant</span>
            </div>
          </div>
        `;
      } else if (productKey === 'wild_berries') {
        legendScaleHtml = `
          <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 8px 10px; margin-top: 8px;">
            <div style="font-size: 0.64rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; margin-bottom: 6px;">
              Wild Berry Ripening Progression Scale
            </div>
            <div style="height: 10px; border-radius: 4px; background: linear-gradient(90deg, #64748b 0%, #cddc39 20%, #81c784 40%, #ba68c8 60%, #7b1fa2 80%, #8d6e63 100%); border: 1px solid rgba(255,255,255,0.12); margin-bottom: 4px;"></div>
            <div style="display: grid; grid-template-columns: repeat(6, 1fr); font-size: 0.58rem; text-align: center; font-weight: 700; color: var(--text-muted);">
              <span>Dormant</span>
              <span>Flower</span>
              <span>Green</span>
              <span style="color: #ba68c8;">Early</span>
              <span style="color: #b388ff;">Peak</span>
              <span style="color: #8d6e63;">Overripe</span>
            </div>
          </div>
        `;
      } else {
        legendScaleHtml = `
          <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 8px 10px; margin-top: 8px;">
            <div style="font-size: 0.64rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; margin-bottom: 6px;">
              Alpine Bloom Progression Scale
            </div>
            <div style="height: 10px; border-radius: 4px; background: linear-gradient(90deg, #a0d2f5 0%, #81c784 20%, #cddc39 40%, #e91e63 65%, #ff9800 85%, #64748b 100%); border: 1px solid rgba(255,255,255,0.12); margin-bottom: 4px;"></div>
            <div style="display: grid; grid-template-columns: repeat(6, 1fr); font-size: 0.58rem; text-align: center; font-weight: 700; color: var(--text-muted);">
              <span>Snow</span>
              <span>Green</span>
              <span>Early</span>
              <span style="color: #ff4081;">Peak</span>
              <span>Past</span>
              <span style="color: #94a3b8;">Ended</span>
            </div>
          </div>
        `;
      }
    } else if (viewMode === 'peak_date') {
      if (productKey === 'desert_wildflowers') {
        legendScaleHtml = `
          <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 8px 10px; margin-top: 8px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
              <div style="font-size: 0.64rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase;">Peak Bloom Arrival Date</div>
              <span style="font-size: 0.58rem; color: var(--text-muted);">Feb 15 – Apr 30</span>
            </div>
            <div style="height: 10px; border-radius: 4px; background: linear-gradient(90deg, #cddc39 0%, #ffd54f 25%, #ff9800 50%, #e91e63 75%, #673ab7 100%); border: 1px solid rgba(255,255,255,0.12); margin-bottom: 4px;"></div>
            <div style="display: flex; justify-content: space-between; font-size: 0.58rem; font-weight: 700; color: var(--text-muted);">
              <span style="color: #cddc39;">Feb 15</span>
              <span style="color: #ffd54f;">Mar 1</span>
              <span style="color: #ff9800;">Mar 20</span>
              <span style="color: #ff4081;">Apr 10</span>
              <span style="color: #b388ff;">Apr 30</span>
            </div>
          </div>
        `;
      } else if (productKey === 'wild_berries') {
        legendScaleHtml = `
          <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 8px 10px; margin-top: 8px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
              <div style="font-size: 0.64rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase;">Peak Sugar Harvest Date</div>
              <span style="font-size: 0.58rem; color: var(--text-muted);">Jun 20 – Sep 30</span>
            </div>
            <div style="height: 10px; border-radius: 4px; background: linear-gradient(90deg, #cddc39 0%, #ffd54f 25%, #ff9800 50%, #ba68c8 75%, #7b1fa2 100%); border: 1px solid rgba(255,255,255,0.12); margin-bottom: 4px;"></div>
            <div style="display: flex; justify-content: space-between; font-size: 0.58rem; font-weight: 700; color: var(--text-muted);">
              <span style="color: #cddc39;">Jun 20</span>
              <span style="color: #ffd54f;">Jul 10</span>
              <span style="color: #ff9800;">Aug 1</span>
              <span style="color: #ba68c8;">Aug 20</span>
              <span style="color: #b388ff;">Sep 15</span>
            </div>
          </div>
        `;
      } else {
        legendScaleHtml = `
          <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 8px 10px; margin-top: 8px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
              <div style="font-size: 0.64rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase;">Peak Arrival Date</div>
              <span style="font-size: 0.58rem; color: var(--text-muted);">May 1 - Aug 20</span>
            </div>
            <div style="height: 10px; border-radius: 4px; background: linear-gradient(90deg, #cddc39 0%, #ffd54f 25%, #ff9800 50%, #e91e63 75%, #673ab7 100%); border: 1px solid rgba(255,255,255,0.12); margin-bottom: 4px;"></div>
            <div style="display: flex; justify-content: space-between; font-size: 0.58rem; font-weight: 700; color: var(--text-muted);">
              <span style="color: #cddc39;">May 15</span>
              <span style="color: #ffd54f;">June 1</span>
              <span style="color: #ff9800;">June 20</span>
              <span style="color: #ff4081;">July 10</span>
              <span style="color: #b388ff;">Aug 1</span>
            </div>
          </div>
        `;
      }
    } else {
      // mbii mode
      if (productKey === 'desert_wildflowers') {
        legendScaleHtml = `
          <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 8px 10px; margin-top: 8px;">
            <div style="font-size: 0.64rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; margin-bottom: 4px;">
              Desert Bloom Intensity Index (BII)
            </div>
            <div style="height: 10px; border-radius: 4px; background: linear-gradient(90deg, rgba(233,30,99,0.15) 0%, #ffd54f 35%, #ff9800 65%, #e91e63 100%); border: 1px solid rgba(255,255,255,0.12); margin-bottom: 4px;"></div>
            <div style="display: flex; justify-content: space-between; font-size: 0.58rem; font-weight: 700; color: var(--text-muted);">
              <span>Sparse (&lt;30)</span>
              <span>Healthy (50)</span>
              <span>Above Avg (75)</span>
              <span style="color: #ff4081;">Superbloom (100)</span>
            </div>
          </div>
        `;
      } else if (productKey === 'wild_berries') {
        legendScaleHtml = `
          <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 8px 10px; margin-top: 8px;">
            <div style="font-size: 0.64rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; margin-bottom: 6px;">
              Crop Quality & Stress Badges
            </div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; font-size: 0.62rem; font-weight: 700;">
              <div style="display: flex; align-items: center; gap: 4px; background: rgba(0,230,118,0.12); padding: 4px 6px; border-radius: 4px; border: 1px solid rgba(0,230,118,0.3); color: #00e676;">
                <span>🟢</span> <span>Prime / Plump</span>
              </div>
              <div style="display: flex; align-items: center; gap: 4px; background: rgba(255,213,79,0.12); padding: 4px 6px; border-radius: 4px; border: 1px solid rgba(255,213,79,0.3); color: #ffd54f;">
                <span>🟡</span> <span>Moderate / Fair</span>
              </div>
              <div style="display: flex; align-items: center; gap: 4px; background: rgba(255,152,0,0.12); padding: 4px 6px; border-radius: 4px; border: 1px solid rgba(255,152,0,0.3); color: #ff9800;">
                <span>🟠</span> <span>Drought-Stressed</span>
              </div>
              <div style="display: flex; align-items: center; gap: 4px; background: rgba(244,67,54,0.12); padding: 4px 6px; border-radius: 4px; border: 1px solid rgba(244,67,54,0.3); color: #f44336;">
                <span>🔴</span> <span>Frost Aborted</span>
              </div>
            </div>
          </div>
        `;
      } else {
        legendScaleHtml = `
          <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 8px 10px; margin-top: 8px;">
            <div style="font-size: 0.64rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; margin-bottom: 4px;">
              Mountain Bloom Intensity Index (MBII)
            </div>
            <div style="height: 10px; border-radius: 4px; background: linear-gradient(90deg, rgba(255,213,79,0.2) 0%, #ffd54f 35%, #ff9800 65%, #e91e63 100%); border: 1px solid rgba(255,255,255,0.12); margin-bottom: 4px;"></div>
            <div style="display: flex; justify-content: space-between; font-size: 0.58rem; font-weight: 700; color: var(--text-muted);">
              <span>Low (25)</span>
              <span>Moderate (50)</span>
              <span>Vibrant (75)</span>
              <span style="color: #ff4081;">Superbloom (100)</span>
            </div>
          </div>
        `;
      }
    }

    // 9. POI Telemetry Banner HTML
    let telemetryHtml = '';
    if (productKey === 'desert_wildflowers') {
      telemetryHtml = `
        <div style="background: rgba(15, 23, 42, 0.7); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; padding: 8px 10px; margin-bottom: 6px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
            <span style="font-size: 0.76rem; font-weight: 800; color: #fff;">📍 Selected Desert Basin</span>
            <span style="font-size: 0.65rem; color: #ff80ab; font-weight: 700; background: rgba(233,30,99,0.2); padding: 1px 6px; border-radius: 4px;">BII: ${profile.biiScore || profile.mbiiScore}/100</span>
          </div>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; font-size: 0.65rem; color: var(--text-muted);">
            <div>Elevation: <strong style="color: #fff;">${Math.round(this.currentElevation)} ft (${Math.round(elevMeters)}m)</strong></div>
            <div>Storm Cadence: <strong style="color: #4dd0e1;">${profile.stormCadenceDays || 12}d Max Gap (Viable)</strong></div>
            <div style="grid-column: span 2;">Autumn Rain Trigger: <strong style="color: #aed581;">${profile.autumnLeachingStatus || 'Primed (≥20mm)'}</strong></div>
          </div>
        </div>
      `;
    } else if (productKey === 'wild_berries') {
      telemetryHtml = `
        <div style="background: rgba(15, 23, 42, 0.7); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; padding: 8px 10px; margin-bottom: 6px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
            <span style="font-size: 0.76rem; font-weight: 800; color: #fff;">📍 Selected Foraging Location</span>
            <span style="font-size: 0.65rem; color: #00e676; font-weight: 700; background: rgba(0,230,118,0.15); padding: 1px 6px; border-radius: 4px;">Quality: ${profile.qualityScore || 88}/100</span>
          </div>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; font-size: 0.65rem; color: var(--text-muted);">
            <div>Elevation: <strong style="color: #fff;">${Math.round(this.currentElevation)} ft (${Math.round(elevMeters)}m)</strong></div>
            <div>Active Wave: <strong style="color: #ba68c8;">${profile.elevationBand || '3,000 - 4,500 ft'}</strong></div>
            <div style="grid-column: span 2;">Crop Badge: <strong style="color: #00e676;">🏅 ${profile.qualityBadge || 'Prime / Plump'}</strong></div>
          </div>
        </div>
      `;
    } else {
      telemetryHtml = `
        <div style="background: rgba(15, 23, 42, 0.7); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; padding: 8px 10px; margin-bottom: 6px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
            <span style="font-size: 0.76rem; font-weight: 800; color: #fff;">📍 Selected Mountain Location</span>
            <span style="font-size: 0.65rem; color: #ff80ab; font-weight: 700; background: rgba(233,30,99,0.2); padding: 1px 6px; border-radius: 4px;">MBII: ${profile.mbiiScore}/100</span>
          </div>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; font-size: 0.65rem; color: var(--text-muted);">
            <div>Elevation: <strong style="color: #fff;">${Math.round(this.currentElevation)} ft (${Math.round(elevMeters)}m)</strong></div>
            <div>Snowmelt Biofix: <strong style="color: #a0d2f5;">Day ${profile.snowmeltDoy || 120}</strong></div>
          </div>
        </div>
      `;
    }

    const chartTitle = productKey === 'desert_wildflowers' 
      ? '📈 Desert Annual Concurrency Waves' 
      : (productKey === 'wild_berries' ? '📈 Berry Ripening & Brix Waves' : '📈 Multi-Species Concurrency Waves');

    const chartSubtitle = productKey === 'desert_wildflowers'
      ? 'January – May'
      : (productKey === 'wild_berries' ? 'June – October' : 'March – August');

    return `
      <div id="gw-wildflower-panel-container" class="gw-wildflower-panel-root" data-product="${productKey}" style="display: flex; flex-direction: column; gap: 4px; width: 100%; box-sizing: border-box; min-width: 0; max-width: 100%; overflow: hidden;">
        ${domainSwitcherHtml}
        ${modeSwitcherHtml}
        ${speciesDropdownHtml}
        ${quickJumpHtml}
        ${telemetryHtml}

        <!-- Month Navigation Tabs -->
        <div style="display: flex; gap: 4px; width: 100%; margin-bottom: 4px;">
          ${tabsHtml}
        </div>

        <!-- Interactive Calendar Grid -->
        <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 6px 4px; margin-bottom: 8px; box-sizing: border-box; overflow: hidden;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
            <span style="font-size: 0.75rem; font-weight: 700; color: #fff;">${monthName} 2026</span>
            <span style="font-size: 0.60rem; color: var(--text-muted);">Click day to view map forecast</span>
          </div>
          ${calendarGridHtml}
        </div>

        <!-- Multi-Species Trend Chart -->
        <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 10px; margin-bottom: 4px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
            <span style="font-size: 0.72rem; font-weight: 800; color: #fff;">${chartTitle}</span>
            <span style="font-size: 0.60rem; color: var(--text-muted);">${chartSubtitle}</span>
          </div>
          ${trendChartSvg}
        </div>

        ${legendScaleHtml}
      </div>
    `;
  }

  private renderTrendChart(profile: WildflowerPoiProfile, productKey: string = 'alpine_wildflowers'): string {
    const width = 280;
    const height = 90;
    const padX = 24;
    const padY = 14;
    const plotW = width - padX * 2;
    const plotH = height - padY * 2;

    const totalDays = productKey === 'desert_wildflowers' ? 151 : (productKey === 'wild_berries' ? 153 : 184);

    const curvesHtml = profile.speciesCurves.map(curve => {
      const stroke = curve.color;
      const strokeWidth = curve.speciesId === 'composite' ? 2.5 : 1.5;
      const strokeDash = curve.isSuitable ? 'none' : '3,3';
      const opacity = curve.isSuitable ? (curve.speciesId === 'composite' ? 1.0 : 0.85) : 0.3;

      let d = '';
      curve.points.forEach((pt, idx) => {
        const x = padX + (idx / (totalDays - 1)) * plotW;
        const y = padY + plotH - (pt.concurrencyPct / 100.0) * plotH;
        if (idx === 0) d += `M ${x.toFixed(1)} ${y.toFixed(1)}`;
        else d += ` L ${x.toFixed(1)} ${y.toFixed(1)}`;
      });

      return `<path d="${d}" fill="none" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-dasharray="${strokeDash}" opacity="${opacity}" />`;
    }).join('');

    let monthMarkers: Array<{ label: string; pos: number }>;
    if (productKey === 'desert_wildflowers') {
      monthMarkers = [
        { label: 'Jan 1', pos: 0.0 },
        { label: 'Feb 1', pos: 31 / 151 },
        { label: 'Mar 1', pos: 59 / 151 },
        { label: 'Apr 1', pos: 90 / 151 },
        { label: 'May 1', pos: 120 / 151 },
        { label: 'May 31', pos: 1.0 }
      ];
    } else if (productKey === 'wild_berries') {
      monthMarkers = [
        { label: 'Jun 1', pos: 0.0 },
        { label: 'Jul 1', pos: 30 / 153 },
        { label: 'Aug 1', pos: 61 / 153 },
        { label: 'Sep 1', pos: 92 / 153 },
        { label: 'Oct 1', pos: 122 / 153 },
        { label: 'Oct 31', pos: 1.0 }
      ];
    } else {
      monthMarkers = [
        { label: 'Mar 1', pos: 0.0 },
        { label: 'Apr 1', pos: 31 / 184 },
        { label: 'May 1', pos: 61 / 184 },
        { label: 'Jun 1', pos: 92 / 184 },
        { label: 'Jul 1', pos: 122 / 184 },
        { label: 'Aug 1', pos: 153 / 184 },
        { label: 'Aug 31', pos: 1.0 }
      ];
    }

    const axisMarkersHtml = monthMarkers.map(m => {
      const x = padX + m.pos * plotW;
      return `
        <line x1="${x.toFixed(1)}" y1="${padY}" x2="${x.toFixed(1)}" y2="${padY + plotH}" stroke="rgba(255,255,255,0.08)" stroke-width="1" />
        <text x="${x.toFixed(1)}" y="${height - 2}" font-size="6.5" fill="rgba(255,255,255,0.4)" text-anchor="middle">${m.label}</text>
      `;
    }).join('');

    const legendItemsHtml = profile.speciesCurves.map(c => `
      <div style="display: flex; align-items: center; gap: 4px; font-size: 0.58rem; color: ${c.isSuitable ? '#fff' : 'rgba(255,255,255,0.4)'};">
        <span style="display: inline-block; width: 8px; height: 3px; background: ${c.color}; border-radius: 1px;"></span>
        <span>${c.name.split(' ')[0]}</span>
      </div>
    `).join('');

    return `
      <div style="width: 100%; display: flex; flex-direction: column; gap: 4px;">
        <svg viewBox="0 0 ${width} ${height}" style="width: 100%; height: auto; overflow: visible;">
          <line x1="${padX}" y1="${padY}" x2="${padX + plotW}" y2="${padY}" stroke="rgba(255,255,255,0.06)" stroke-width="1" />
          <line x1="${padX}" y1="${padY + plotH * 0.5}" x2="${padX + plotW}" y2="${padY + plotH * 0.5}" stroke="rgba(255,255,255,0.06)" stroke-width="1" />
          <line x1="${padX}" y1="${padY + plotH}" x2="${padX + plotW}" y2="${padY + plotH}" stroke="rgba(255,255,255,0.15)" stroke-width="1" />
          
          ${axisMarkersHtml}
          ${curvesHtml}
        </svg>
        <div style="display: flex; flex-wrap: wrap; gap: 6px; justify-content: center; margin-top: 2px;">
          ${legendItemsHtml}
        </div>
      </div>
    `;
  }

  public bindEvents(overlay: HTMLElement): void {
    const roots = overlay.querySelectorAll('.gw-wildflower-panel-root');
    const containers = roots.length > 0 ? Array.from(roots) : [overlay];

    containers.forEach(container => {
      const productKey = (container as HTMLElement).getAttribute('data-product') || 'alpine_wildflowers';

      const updateProduct = (partial: any) => {
        const updates: any = {};
        if (productKey === 'desert_wildflowers') {
          if (partial.domain !== undefined) updates.desertWildflowerDomain = partial.domain;
          if (partial.viewMode !== undefined) updates.desertWildflowerViewMode = partial.viewMode;
          if (partial.speciesId !== undefined) updates.desertWildflowerSpecies = partial.speciesId;
          if (partial.selectedDateStr !== undefined) updates.desertWildflowerSelectedDate = partial.selectedDateStr;
          if (partial.monthIndex !== undefined) updates.desertWildflowerMonthIndex = partial.monthIndex;
        } else if (productKey === 'wild_berries') {
          if (partial.domain !== undefined) updates.wildBerryDomain = partial.domain;
          if (partial.viewMode !== undefined) updates.wildBerryViewMode = partial.viewMode;
          if (partial.speciesId !== undefined) updates.wildBerrySpecies = partial.speciesId;
          if (partial.selectedDateStr !== undefined) updates.wildBerrySelectedDate = partial.selectedDateStr;
          if (partial.monthIndex !== undefined) updates.wildBerryMonthIndex = partial.monthIndex;
        } else {
          if (partial.domain !== undefined) {
            updates.alpineWildflowerDomain = partial.domain;
            updates.wildflowerDomain = partial.domain;
          }
          if (partial.viewMode !== undefined) {
            updates.alpineWildflowerViewMode = partial.viewMode;
            updates.wildflowerViewMode = partial.viewMode;
          }
          if (partial.speciesId !== undefined) {
            updates.alpineWildflowerSpecies = partial.speciesId;
            updates.wildflowerSpecies = partial.speciesId;
          }
          if (partial.selectedDateStr !== undefined) {
            updates.alpineWildflowerSelectedDate = partial.selectedDateStr;
            updates.wildflowerSelectedDate = partial.selectedDateStr;
          }
          if (partial.monthIndex !== undefined) {
            updates.alpineWildflowerMonthIndex = partial.monthIndex;
            updates.wildflowerMonthIndex = partial.monthIndex;
          }
        }
        store.update(updates);
      };

      // 1. Domain toggle
      container.querySelectorAll('.gw-wf-domain-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const d = (btn as HTMLElement).getAttribute('data-domain');
          if (d) updateProduct({ domain: d });
        });
      });

      // 2. View Mode toggle
      container.querySelectorAll('.gw-wf-mode-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const m = (btn as HTMLElement).getAttribute('data-mode');
          if (m) updateProduct({ viewMode: m });
        });
      });

      // 3. Species select
      const spSelect = container.querySelector('.gw-wf-species-select') as HTMLSelectElement | null;
      if (spSelect) {
        spSelect.addEventListener('change', () => {
          const val = spSelect.value;
          updateProduct({ speciesId: val });
        });
      }

      // 4. Quick Jump buttons
      container.querySelectorAll('.gw-wf-quick-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const d = (btn as HTMLElement).getAttribute('data-date');
          if (d) updateProduct({ selectedDateStr: d, viewMode: 'status' });
        });
      });

      // 5. Month tab clicks
      container.querySelectorAll('.gw-wf-month-tab').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const idx = parseInt((btn as HTMLElement).getAttribute('data-month-idx') || '0', 10);
          updateProduct({ monthIndex: idx });
        });
      });

      // 6. Day cell clicks
      container.querySelectorAll('.gw-wf-day-cell').forEach(cell => {
        cell.addEventListener('click', (e) => {
          e.stopPropagation();
          const dateStr = (cell as HTMLElement).getAttribute('data-date');
          if (dateStr) updateProduct({ selectedDateStr: dateStr, viewMode: 'status' });
        });
      });
    });
  }
}
